import { SlideObject } from '../types';
import { constrainMoveDelta, resolveShiftAxis } from './objectArrange';
import { cloneSelection, scaleGrouped, selectionBounds } from './objectGroup';
import {
  aabbDeltaToLocal,
  applyCropPan,
  applyHandleResize,
  applyImageScaleFromHandle,
  applyRotation,
  applySideCrop,
  mapVisualHandleToLocal,
  ResizeHandle,
  rotationFromPointer,
  screenDeltaToLocal,
} from './objectTransform';
import { SmartGuideLine, snapMoveWithGuides, snapResizeWithGuides } from './smartGuides';

export const MOVE_THRESHOLD_PX = 4;

export type DragMode = 'move' | 'resize' | 'crop' | 'rotate' | 'pan' | 'scaleImage';

export type PointerSample = {
  clientX: number;
  clientY: number;
  shiftKey?: boolean;
  altKey?: boolean;
};

export type DragContext = {
  scale: number;
  snap: boolean;
  smartGuides?: boolean;
  pageSize?: { width: number; height: number };
  rotationCenter?: { x: number; y: number };
};

export type DragPreview = {
  objects: SlideObject[];
  selectedId: string;
  selectedIds?: string[];
  guides?: SmartGuideLine[];
};

export type DragSession = {
  id: string;
  startX: number;
  startY: number;
  origX: number;
  origY: number;
  origW: number;
  origH: number;
  origStyle?: SlideObject['style'];
  mode: DragMode;
  handle?: ResizeHandle;
  startAngle: number;
  startRotation: number;
  historyStarted: boolean;
  sourceId: string;
  altCopy: boolean;
  altSeeds?: SlideObject[];
  shiftAxis: 'x' | 'y' | null;
  baseline: SlideObject[];
  selectedId: string;
  companionIds: string[];
};

export function canStartDrag(object: SlideObject, mode: DragMode, readOnly = false): boolean {
  if (readOnly) return false;
  if (mode === 'move' && object.locks.position) return false;
  if ((mode === 'resize' || mode === 'crop' || mode === 'scaleImage') && object.locks.size) return false;
  return true;
}

export function crossedMoveThreshold(session: Pick<DragSession, 'startX' | 'startY'>, pointer: PointerSample): boolean {
  return Math.hypot(pointer.clientX - session.startX, pointer.clientY - session.startY) > MOVE_THRESHOLD_PX;
}

export function startDragSession(input: {
  object: SlideObject;
  objects: SlideObject[];
  pointer: PointerSample;
  mode: DragMode;
  handle?: ResizeHandle;
  startAngle?: number;
  companionIds?: string[];
}): DragSession {
  const companionIds = (input.companionIds || []).filter((id) => id !== input.object.id);
  const groupBox = input.mode === 'resize' && companionIds.length
    ? selectionBounds(input.objects, [input.object.id, ...companionIds])
    : null;
  return {
    id: input.object.id,
    startX: input.pointer.clientX,
    startY: input.pointer.clientY,
    origX: groupBox?.x ?? input.object.x,
    origY: groupBox?.y ?? input.object.y,
    origW: groupBox?.w ?? input.object.w,
    origH: groupBox?.h ?? input.object.h,
    origStyle: input.object.style,
    mode: input.mode,
    handle: input.handle,
    startAngle: input.startAngle ?? 0,
    startRotation: input.object.style?.rotation || 0,
    historyStarted: false,
    sourceId: input.object.id,
    altCopy: false,
    shiftAxis: null,
    baseline: input.objects,
    selectedId: input.object.id,
    companionIds,
  };
}

function replaceObject(objects: SlideObject[], id: string, next: SlideObject): SlideObject[] {
  return objects.map((object) => (object.id === id ? next : object));
}

function sourceObject(session: DragSession): SlideObject {
  return session.baseline.find((object) => object.id === session.sourceId)
    || session.baseline.find((object) => object.id === session.id)
    || session.baseline[0];
}

export function advanceDragSession(
  session: DragSession,
  pointer: PointerSample,
  ctx: DragContext,
): { session: DragSession; preview: DragPreview | null; justStartedHistory: boolean } {
  const justStartedHistory = !session.historyStarted && crossedMoveThreshold(session, pointer);
  if (!session.historyStarted && !justStartedHistory) {
    return { session, preview: null, justStartedHistory: false };
  }

  const next: DragSession = { ...session, historyStarted: true };
  const scale = ctx.scale || 1;
  const dx = (pointer.clientX - session.startX) / scale;
  const dy = (pointer.clientY - session.startY) / scale;
  const source = sourceObject(session);

  if (session.mode === 'move') {
    next.shiftAxis = pointer.shiftKey ? resolveShiftAxis(dx, dy, session.shiftAxis) : null;
    const move = constrainMoveDelta(dx, dy, next.shiftAxis);
    const rawX = session.origX + move.dx;
    const rawY = session.origY + move.dy;
    const companions = new Set(session.companionIds);
    const moving = session.baseline.filter((object) => object.id === session.id || companions.has(object.id));
    const targets = session.baseline.filter((object) => object.id !== session.id && !companions.has(object.id));
    const guided = snapMoveWithGuides({
      rawX,
      rawY,
      origX: session.origX,
      origY: session.origY,
      moving,
      targets,
      pageSize: ctx.pageSize,
      snapGrid: ctx.snap,
      enabled: Boolean(ctx.smartGuides),
      axes: { x: next.shiftAxis !== 'y', y: next.shiftAxis !== 'x' },
    });
    let nextX = guided.x;
    let nextY = guided.y;
    let guides = guided.lines;

    if (pointer.altKey && !session.altCopy && !source.locks.delete) {
      const movingIds = new Set([session.sourceId, ...session.companionIds]);
      const toCopy = session.baseline
        .filter((object) => movingIds.has(object.id) && !object.locks.delete)
        .map((object) => (object.id === session.sourceId
          ? { ...object, x: session.origX, y: session.origY }
          : object));
      const copies = cloneSelection(toCopy, session.baseline, { x: 0, y: 0 });
      const primaryIndex = Math.max(0, toCopy.findIndex((object) => object.id === session.sourceId));
      const primary = copies[primaryIndex] || copies[0];
      if (primary) {
        next.altCopy = true;
        next.altSeeds = copies;
        next.id = primary.id;
        next.selectedId = primary.id;
      }
    }

    if (next.altCopy && next.altSeeds?.length) {
      const copyGuided = snapMoveWithGuides({
        rawX,
        rawY,
        origX: session.origX,
        origY: session.origY,
        moving: next.altSeeds,
        targets: session.baseline,
        pageSize: ctx.pageSize,
        snapGrid: ctx.snap,
        enabled: Boolean(ctx.smartGuides),
        axes: { x: next.shiftAxis !== 'y', y: next.shiftAxis !== 'x' },
      });
      nextX = copyGuided.x;
      nextY = copyGuided.y;
      guides = copyGuided.lines;
      const deltaX = nextX - session.origX;
      const deltaY = nextY - session.origY;
      const moved = next.altSeeds.map((object) => ({
        ...object,
        x: object.x + deltaX,
        y: object.y + deltaY,
      }));
      return {
        session: next,
        preview: {
          objects: [...session.baseline, ...moved],
          selectedId: next.selectedId,
          selectedIds: moved.map((object) => object.id),
          guides,
        },
        justStartedHistory,
      };
    }

    const current = session.baseline.find((object) => object.id === session.id) || source;
    const deltaX = nextX - session.origX;
    const deltaY = nextY - session.origY;
    return {
      session: next,
      preview: {
        objects: session.baseline.map((object) => {
          if (object.id === session.id) return { ...current, x: nextX, y: nextY };
          if (!companions.has(object.id) || object.locks.position) return object;
          return { ...object, x: object.x + deltaX, y: object.y + deltaY };
        }),
        selectedId: next.selectedId,
        guides,
      },
      justStartedHistory,
    };
  }

  const current = session.baseline.find((object) => object.id === session.id) || source;

  if (session.mode === 'pan') {
    const rotation = session.origStyle?.rotation || 0;
    const local = screenDeltaToLocal(dx, dy, rotation);
    const visW = Math.max(0.08, 1 - (session.origStyle?.cropLeft || 0) - (session.origStyle?.cropRight || 0));
    const visH = Math.max(0.08, 1 - (session.origStyle?.cropTop || 0) - (session.origStyle?.cropBottom || 0));
    const patched = {
      ...current,
      style: applyCropPan(session.origStyle, (-local.dx / session.origW) * visW, (-local.dy / session.origH) * visH),
    };
    return {
      session: next,
      preview: { objects: replaceObject(session.baseline, session.id, patched), selectedId: next.selectedId },
      justStartedHistory,
    };
  }

  if (session.mode === 'rotate') {
    if (!ctx.rotationCenter) {
      return { session: next, preview: null, justStartedHistory };
    }
    const angle = rotationFromPointer(ctx.rotationCenter.x, ctx.rotationCenter.y, pointer.clientX, pointer.clientY);
    const patched = {
      ...current,
      style: {
        ...current.style,
        rotation: applyRotation(session.startRotation, session.startAngle, angle, !pointer.shiftKey),
      },
    };
    return {
      session: next,
      preview: { objects: replaceObject(session.baseline, session.id, patched), selectedId: next.selectedId },
      justStartedHistory,
    };
  }

  const draft = {
    ...current,
    x: session.origX,
    y: session.origY,
    w: session.origW,
    h: session.origH,
    style: session.origStyle,
  };

  if (session.mode === 'crop' && session.handle) {
    const rotation = session.origStyle?.rotation || 0;
    const localHandle = mapVisualHandleToLocal(session.handle, rotation);
    const local = aabbDeltaToLocal(dx, dy, rotation);
    const patched = { ...current, ...applySideCrop(draft, localHandle, local.dx, local.dy, ctx.snap) };
    return {
      session: next,
      preview: { objects: replaceObject(session.baseline, session.id, patched), selectedId: next.selectedId },
      justStartedHistory,
    };
  }

  if (session.mode === 'scaleImage' && session.handle) {
    const rotation = session.origStyle?.rotation || 0;
    const local = screenDeltaToLocal(dx, dy, rotation);
    const patched = { ...current, ...applyImageScaleFromHandle(draft, session.handle, local.dx, local.dy) };
    return {
      session: next,
      preview: { objects: replaceObject(session.baseline, session.id, patched), selectedId: next.selectedId },
      justStartedHistory,
    };
  }

  if (session.handle) {
    const rotation = session.companionIds.length ? 0 : (session.origStyle?.rotation || 0);
    const localHandle = mapVisualHandleToLocal(session.handle, rotation);
    const local = aabbDeltaToLocal(dx, dy, rotation);
    if (session.companionIds.length) {
      const from = { x: session.origX, y: session.origY, w: session.origW, h: session.origH };
      const rawTo = applyHandleResize(
        { x: from.x, y: from.y, w: from.w, h: from.h, type: 'shape' },
        localHandle,
        local.dx,
        local.dy,
        { uniform: localHandle.length === 2, snap: false, minSize: 40 },
      );
      const guided = snapResizeWithGuides({
        box: { x: rawTo.x, y: rawTo.y, w: rawTo.w, h: rawTo.h },
        targets: session.baseline.filter((object) => object.id !== session.id && !session.companionIds.includes(object.id)),
        pageSize: ctx.pageSize,
        snapGrid: ctx.snap,
        enabled: Boolean(ctx.smartGuides),
        handle: localHandle,
        minSize: 40,
      });
      return {
        session: next,
        preview: {
          objects: scaleGrouped(session.baseline, [session.id, ...session.companionIds], from, guided.box),
          selectedId: next.selectedId,
          guides: guided.lines,
        },
        justStartedHistory,
      };
    }
    const minSize = current.style?.shape === 'line' ? 8 : 40;
    const rawResized = applyHandleResize(draft, localHandle, local.dx, local.dy, {
      uniform: localHandle.length === 2,
      snap: false,
      minSize,
    });
    const guided = snapResizeWithGuides({
      box: { x: rawResized.x, y: rawResized.y, w: rawResized.w, h: rawResized.h },
      targets: session.baseline.filter((object) => object.id !== session.id),
      pageSize: ctx.pageSize,
      snapGrid: ctx.snap,
      enabled: Boolean(ctx.smartGuides),
      handle: localHandle,
      rotation: session.origStyle?.rotation || 0,
      minSize,
    });
    const patched = {
      ...current,
      ...rawResized,
      ...guided.box,
      style: {
        ...current.style,
        ...rawResized.style,
        ...(current.type === 'text' ? { textFit: 'box' as const } : null),
      },
    };
    return {
      session: next,
      preview: { objects: replaceObject(session.baseline, session.id, patched), selectedId: next.selectedId, guides: guided.lines },
      justStartedHistory,
    };
  }

  return { session: next, preview: null, justStartedHistory };
}

export function shouldCommitDrag(session: DragSession | null): boolean {
  return Boolean(session?.historyStarted);
}

export function createRafScheduler(
  raf: (cb: FrameRequestCallback) => number = (cb) => requestAnimationFrame(cb),
  cancel: (id: number) => void = (id) => cancelAnimationFrame(id),
) {
  let id: number | null = null;
  let pending: (() => void) | null = null;

  return {
    schedule(fn: () => void) {
      pending = fn;
      if (id != null) return;
      id = raf(() => {
        id = null;
        const run = pending;
        pending = null;
        run?.();
      });
    },
    flush() {
      if (id != null) {
        cancel(id);
        id = null;
      }
      const run = pending;
      pending = null;
      run?.();
    },
    cancelPending() {
      if (id != null) {
        cancel(id);
        id = null;
      }
      pending = null;
    },
    hasPending() {
      return pending != null || id != null;
    },
  };
}

export function createDragController(options?: {
  raf?: (cb: FrameRequestCallback) => number;
  cancelRaf?: (id: number) => void;
}) {
  const scheduler = createRafScheduler(options?.raf, options?.cancelRaf);
  let session: DragSession | null = null;
  let pending: { pointer: PointerSample; ctx: DragContext } | null = null;
  let preview: DragPreview | null = null;
  let historyCallback: (() => void) | null = null;

  const applyPending = () => {
    if (!session || !pending) return preview;
    const result = advanceDragSession(session, pending.pointer, pending.ctx);
    session = result.session;
    if (result.justStartedHistory) historyCallback?.();
    if (result.preview) preview = result.preview;
    pending = null;
    return preview;
  };

  const reset = () => {
    session = null;
    pending = null;
    preview = null;
    historyCallback = null;
  };

  return {
    isActive() {
      return session != null;
    },
    getSession() {
      return session;
    },
    getPreview() {
      return preview;
    },
    start(input: Parameters<typeof startDragSession>[0], onHistoryBegin?: () => void) {
      scheduler.cancelPending();
      session = startDragSession(input);
      pending = null;
      preview = null;
      historyCallback = onHistoryBegin || null;
    },
    move(pointer: PointerSample, ctx: DragContext, onFrame: (next: DragPreview) => void) {
      if (!session) return;
      pending = { pointer, ctx };
      scheduler.schedule(() => {
        const next = applyPending();
        if (next) onFrame(next);
      });
    },
    finish(): DragPreview | null {
      scheduler.flush();
      if (pending) applyPending();
      const commit = session && session.historyStarted && preview ? preview : null;
      reset();
      return commit;
    },
    discard() {
      scheduler.cancelPending();
      reset();
    },
  };
}
